import { useEffect, useState } from "react";
import { Menu, X, Moon, Sun, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";
import { NAV_ITEMS, BRAND } from "@/constants/brand";
import { useTheme } from "@/contexts/ThemeContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-strong" : "bg-transparent"
        }`}
      data-testid="site-navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 md:px-10 md:py-4">
        <Logo className="h-14 w-auto md:h-16" testId="nav-logo" />

        <ul className="hidden items-center gap-8 lg:flex">
          {NAV_ITEMS.map((item) => (
            <li key={item.href}>
              <button
                onClick={() => handleNav(item.href)}
                className="text-sm font-medium text-foreground/70 transition-colors hover:text-foreground"
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border transition-all hover:border-[hsl(var(--brand-orange))] hover:text-[hsl(var(--brand-orange))]"
            data-testid="theme-toggle-btn"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ y: -8, opacity: 0, rotate: -90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: 8, opacity: 0, rotate: 90 }}
                transition={{ duration: 0.2 }}
                className="inline-flex"
              >
                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              </motion.span>
            </AnimatePresence>
          </button>

          <a
            href={BRAND.socials.telegram}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-brand hidden text-sm md:inline-flex"
            data-testid="nav-cta-telegram"
          >
            <Send size={16} />
            Join Telegram
          </a>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border lg:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden lg:hidden glass-strong"
            data-testid="mobile-menu"
          >
            <ul className="mx-auto flex max-w-7xl flex-col gap-1 px-5 pb-4 pt-2">
              {NAV_ITEMS.map((item) => (
                <li key={item.href}>
                  <button
                    onClick={() => handleNav(item.href)}
                    className="w-full rounded-lg px-3 py-3 text-left text-base font-medium text-foreground/80 transition-colors hover:bg-[hsl(var(--brand-orange)/0.08)] hover:text-[hsl(var(--brand-orange))]"
                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href={BRAND.socials.telegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-brand mt-2 w-full justify-center"
                  data-testid="mobile-cta-telegram"
                >
                  <Send size={16} /> Join Telegram
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
